import { ColorOverlayFilter } from "@pixi/filter-color-overlay";
import { Container, Graphics } from "@pixi/react";
import { PlaceFillRoundedWhite } from "./SpriteIndex";
import BoundingBox from "../../../Domain/Types/BoundingBox.type";
import { PlaceVariant } from "../../../Domain/Types/FloorPlan/PlaceVariant.type";
import { ToolSelection } from "../../Views/CreateFloorPlanView/CreateFloorPlanView";
import { drawPlaceColor } from "../functions/drawPlaceColor.function";
import { FavoriteStarGraphic, FAVORITE_STAR_OUTER_BASE } from "../../FavoriteStarGraphic";
import { TintedPlaceCircleSprite, TintedPlaceRectSprite } from "./PlaceSpriteTintedBody";
import { drawPartialOccupancyPie } from "./drawPartialOccupancyPie.function";
import { drawBlockedProhibitionMark } from "./drawBlockedProhibitionMark.function";

interface Props {
  boundingBox: BoundingBox;
  filterGroup: ColorOverlayFilter[];
  variantColor: number;
  variant: PlaceVariant;
  placeScale: number;
  isSelectable: boolean;
  isHighlighted?: boolean;
  isFavorite?: boolean;
  colorOverlay: number | undefined;
  compatibilityFill?: number;
  tool?: ToolSelection;
  isColleaguesPlace?: boolean;
  isGrpPreferred: boolean;
  /** Desk angle in degrees. The partial pie is drawn against this so the split stays vertical. */
  rotation?: number;
}

/**
 * draw the workplace sprite by pixi-sprite or pixi-graphic according to module(booking/creating)
 * @version 1.0.0
 */
export function WorkPlaceSprite({
  boundingBox,
  filterGroup,
  variantColor,
  variant,
  placeScale,
  isHighlighted,
  isFavorite = false,
  colorOverlay,
  compatibilityFill,
  isSelectable,
  tool,
  isColleaguesPlace,
  isGrpPreferred,
  rotation = 0
}: Readonly<Props>) {
  const { width = 160, height = 80 } = boundingBox;

  const favoriteStarScale = Math.min(0.85, Math.max(0.38, placeScale * 0.12));
  const wS = width * placeScale;
  const starOuter = FAVORITE_STAR_OUTER_BASE * favoriteStarScale;
  const inset = 5 * placeScale;
  const favoriteStarPosition = {
    x: wS / 2 - inset - starOuter / 2,
    y: -16 * placeScale + inset + starOuter / 2
  };

  const circleRadius = width * placeScale * 0.2;
  const lineWidth = width * placeScale * 0.04;

  const { rectColor, circColor } = drawPlaceColor({
    isHighlighted: isHighlighted ?? false,
    colorOverlay,
    variantColor,
    tool,
    circleColorOverride: compatibilityFill
  });

  return (
    <>
      {isGrpPreferred ? (
        <WorkplaceSpriteGraphic
          isSelectable={isSelectable}
          width={width}
          height={height}
          placeScale={placeScale}
          filterGroup={filterGroup}
          isColleaguesPlace={isColleaguesPlace}
          isPartial={variant === PlaceVariant.PARTIAL}
          isBlocked={variant === PlaceVariant.BLOCKED}
          rectColor={rectColor}
          lineWidth={lineWidth}
          circColor={circColor}
          circleRadius={circleRadius}
          rotation={rotation}
        />
      ) : (
        <WorkplaceSpriteSvgImg
          isSelectable={isSelectable}
          width={width}
          height={height}
          placeScale={placeScale}
          filterGroup={filterGroup}
          isColleaguesPlace={isColleaguesPlace}
          isPartial={variant === PlaceVariant.PARTIAL}
          isBlocked={variant === PlaceVariant.BLOCKED}
          rectColor={rectColor}
          circColor={circColor}
          circleRadius={circleRadius}
          rotation={rotation}
        />
      )}
      {isFavorite && (
        <Container position={favoriteStarPosition}>
          <FavoriteStarGraphic starScale={favoriteStarScale} />
        </Container>
      )}
    </>
  );
}

type WorkplaceSpriteSvgImgProps = {
  isSelectable: boolean;
  width: number;
  height: number;
  placeScale: number;
  filterGroup: ColorOverlayFilter[];
  isColleaguesPlace?: boolean;
  isPartial?: boolean;
  isBlocked?: boolean;
  rectColor: number;
  circColor: number;
  circleRadius: number;
  rotation?: number;
};

/** Tinted rounded fill + tinted status circle (all views except Firefox). */
export function WorkplaceSpriteSvgImg({
  isSelectable,
  width,
  height,
  placeScale,
  filterGroup,
  isColleaguesPlace,
  isPartial = false,
  isBlocked = false,
  rectColor,
  circColor,
  circleRadius,
  rotation = 0
}: Readonly<WorkplaceSpriteSvgImgProps>) {
  const scaledW = width * placeScale;
  const scaledH = height * placeScale;
  const rectY = -16 * placeScale;
  const radius = circleRadius * (isColleaguesPlace ? 2.2 : 1);
  const diameter = radius * 2;
  const circleTint = isColleaguesPlace ? 0xff0000 : circColor;
  const lineWidth = width * placeScale * 0.04;

  return (
    <>
      <TintedPlaceRectSprite
        testId="workplace-spr-rect"
        image={PlaceFillRoundedWhite}
        tint={rectColor}
        x={0 - scaledW / 2}
        y={rectY}
        width={scaledW}
        height={scaledH}
        isSelectable={isSelectable}
        filterGroup={filterGroup}
      />
      {isPartial ? (
        <Graphics
          data-testid="workplace-spr-circ-partial"
          cursor={isSelectable ? "pointer" : "auto"}
          position={{ x: 0, y: rectY }}
          filters={filterGroup}
          angle={-rotation}
          draw={g => {
            drawPartialOccupancyPie(g, radius, lineWidth);
          }}
        />
      ) : isBlocked ? (
        <Graphics
          data-testid="workplace-spr-circ-blocked"
          cursor={isSelectable ? "pointer" : "auto"}
          position={{ x: 0, y: rectY }}
          filters={filterGroup}
          draw={g => {
            drawBlockedProhibitionMark(g, radius, lineWidth);
          }}
        />
      ) : (
        <TintedPlaceCircleSprite
          testId={isColleaguesPlace ? "workplace-colleagues-spr-circ" : "workplace-spr-circ"}
          tint={circleTint}
          diameter={diameter}
          x={0}
          y={rectY}
          isSelectable={isSelectable}
          filterGroup={filterGroup}
        />
      )}
    </>
  );
}

type WorkplaceSpriteGraphicProps = {
  isSelectable: boolean;
  width: number;
  height: number;
  placeScale: number;
  filterGroup: ColorOverlayFilter[];
  isColleaguesPlace?: boolean;
  isPartial?: boolean;
  isBlocked?: boolean;
  rectColor: number;
  lineWidth: number;
  circColor: number;
  circleRadius: number;
  rotation?: number;
};

export function WorkplaceSpriteGraphic({
  isSelectable,
  width,
  height,
  placeScale,
  filterGroup,
  isColleaguesPlace,
  isPartial = false,
  isBlocked = false,
  rectColor,
  lineWidth,
  circColor,
  circleRadius,
  rotation = 0
}: Readonly<WorkplaceSpriteGraphicProps>) {
  const radius = circleRadius * (isColleaguesPlace ? 2.2 : 1);

  return (
    <>
      <Graphics
        data-testid="workplace-grp-rect"
        cursor={isSelectable ? "pointer" : "auto"}
        position={{ x: 0 - (width * placeScale) / 2, y: -16 * placeScale }}
        draw={g => {
          g.clear();
          g.beginFill(rectColor, 1);
          g.drawRoundedRect(0, 0, width * placeScale, height * placeScale, 13 * (placeScale / 2));
          g.endFill();
        }}
        filters={filterGroup}
      />
      <Graphics
        data-testid="workplace-grp-circ"
        cursor={isSelectable ? "pointer" : "auto"}
        position={{ x: 0, y: -16 * placeScale }}
        filters={filterGroup}
        {...(isPartial ? { angle: -rotation } : {})}
        draw={g => {
          if (isPartial) {
            drawPartialOccupancyPie(g, radius, lineWidth);
            return;
          }
          if (isBlocked) {
            drawBlockedProhibitionMark(g, radius, lineWidth);
            return;
          }
          g.clear();
          g.lineStyle(lineWidth, 0xffffff);
          g.beginFill(isColleaguesPlace ? 0xff0000 : circColor, 1);
          g.drawCircle(0, 0, radius);
          g.endFill();
        }}
      />
    </>
  );
}

/** example of cube container */
export const LockerContainerExample = ({
  isSelectable,
  placeScale,
  filterGroup,
  lineWidth
}: {
  isSelectable: boolean;
  placeScale: number;
  filterGroup: ColorOverlayFilter[];
  lineWidth: number;
}) => {
  return (
    <Graphics
      data-testid="locker-container-grp"
      cursor={isSelectable ? "pointer" : "auto"}
      position={{ x: 0, y: -16 * placeScale }}
      filters={filterGroup}
      draw={g => {
        g.clear();
        g.lineStyle(lineWidth / 4, 0xffffff);
        g.beginFill("0x808080", 1);
        g.drawPolygon([
          { x: 0, y: 0 },
          { x: -20, y: 30 },
          { x: 30, y: 95 },
          { x: 50, y: 70 }
        ]);
        g.drawPolygon([
          { x: 0, y: 0 },
          { x: 15, y: -5 },
          { x: 65, y: 65 },
          { x: 50, y: 70 }
        ]);
        g.drawPolygon([
          { x: 50, y: 70 },
          { x: 65, y: 65 },
          { x: 55, y: 80 },
          { x: 30, y: 95 }
        ]);
        g.endFill();
      }}
    />
  );
};
